/**
 * NotificationBell — header dropdown showing unread severe-weather alerts.
 *
 * Signed-in only. Polls `/notifications/unread-count` every 60s and refreshes
 * the list when opened. Clicking an alert marks it read + navigates to the
 * Weather Almanac.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, Loader2, Send } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const POLL_MS = 60_000;

// Where a notification of a given `kind` should land when clicked.
function targetFor(n) {
  const kind = n.kind || "weather_alert";
  if (kind === "message") {
    const uid = n?.meta?.from_user_id;
    return uid ? `/messages?to=${uid}` : "/messages";
  }
  if (kind === "connection_request" || kind === "connection_accepted") {
    return "/notifications";
  }
  // ChatPulse / Publisher discussions — deep-link to the source page
  // (article, event, or thread canonical URL) so the click drops the
  // reader straight into context.
  if (kind === "chat_reply" || kind === "article_reply" || kind === "thread_reply") {
    const url = n?.meta?.url;
    if (url) return url; // full canonical URL — Link handles external via `to`
    const cType = n?.meta?.context_type;
    const cId = n?.meta?.context_id;
    if (cType === "article") return `/article/${cId}`;
    if (cType === "event") return `/event/${cId}`;
    if (cType === "location") return `/l/${(cId || "").replace(/-/g, "/")}`;
    return "/chat";
  }
  return "/weather";
}

export default function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null); // notification id
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const boxRef = useRef(null);

  const refreshCount = async () => {
    if (!user) { setCount(0); return; }
    try {
      const { data } = await http.get("/notifications/unread-count");
      setCount(data.unread_count || 0);
    } catch { /* ignore */ }
  };

  const refreshList = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await http.get("/notifications?limit=15");
      setItems(data.notifications || []);
      setCount(data.unread_count || 0);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!user) { setCount(0); setItems([]); return; }
    refreshCount();
    const id = setInterval(refreshCount, POLL_MS);
    return () => clearInterval(id);
  }, [user]);

  useEffect(() => {
    if (open) refreshList();
  }, [open]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  const markRead = async (id) => {
    try { await http.post("/notifications/mark-read", { id }); } catch { /* ignore */ }
    setItems((rows) => rows.map((r) => r.id === id ? { ...r, read_at: new Date().toISOString() } : r));
    setCount((c) => Math.max(0, c - 1));
  };

  const markAll = async () => {
    try { await http.post("/notifications/mark-all-read", {}); } catch { /* ignore */ }
    setItems((rows) => rows.map((r) => ({ ...r, read_at: r.read_at || new Date().toISOString() })));
    setCount(0);
  };

  const startReply = (n) => {
    setReplyingTo(replyingTo === n.id ? null : n.id);
    setReplyText("");
  };

  const sendReply = async (n) => {
    const toId = n?.meta?.from_user_id;
    if (!toId || !replyText.trim()) return;
    setSending(true);
    try {
      await http.post("/messages/send", { to_user_id: toId, body: replyText.trim() });
      toast.success("Reply sent");
      setReplyText("");
      setReplyingTo(null);
      if (!n.read_at) markRead(n.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send reply");
    } finally {
      setSending(false);
    }
  };

  if (!user) return null;

  return (
    <div className="relative" ref={boxRef} data-testid="notification-bell">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-full hover:bg-black/[0.04] transition"
        data-testid="notification-bell-btn"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {count > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full text-[10px] font-mono font-bold flex items-center justify-center px-1"
            style={{ background: "var(--wofy-primary)", color: "#fff" }}
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 mt-2 w-[360px] max-w-[92vw] rounded-2xl shadow-lg z-50 overflow-hidden"
          style={{ background: "var(--wofy-bg)", border: "1px solid var(--wofy-border)" }}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--wofy-border)" }}>
            <div className="font-display text-lg">Notifications</div>
            {count > 0 && (
              <button onClick={markAll} data-testid="notification-mark-all" className="text-xs underline" style={{ color: "var(--wofy-text-soft)" }}>
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {loading && <p className="p-4 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>}
            {!loading && items.length === 0 && (
              <p className="p-6 text-sm text-center" style={{ color: "var(--wofy-text-soft)" }}>
                No notifications yet. When severe weather approaches your locality, you'll see it here.
              </p>
            )}
            {items.map((n) => {
              const isMsg = n.kind === "message" && n?.meta?.from_user_id;
              const isReplying = replyingTo === n.id;
              return (
                <div
                  key={n.id}
                  data-testid={`notification-item-${n.id}`}
                  className={`block px-4 py-3 border-b transition ${n.read_at ? "opacity-70" : ""}`}
                  style={{ borderColor: "var(--wofy-border)" }}
                >
                  <Link
                    to={targetFor(n)}
                    onClick={(e) => {
                      // Don't navigate if the user is interacting with the reply UI.
                      if (isReplying) { e.preventDefault(); return; }
                      if (!n.read_at) markRead(n.id);
                      setOpen(false);
                    }}
                    className="block"
                  >
                    <div className="flex items-start gap-2">
                      <span
                        className={`mt-0.5 inline-block w-2 h-2 rounded-full ${n.read_at ? "" : ""}`}
                        style={{ background: n.severity === "critical" ? "#dc2626" : n.severity === "severe" ? "#f97316" : "#f59e0b" }}
                        aria-hidden
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                          <span>{n.headline}</span>
                          {n.count > 1 && (
                            <span
                              className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full"
                              style={{ background: "var(--wofy-primary)", color: "white" }}
                              data-testid={`notif-count-${n.id}`}
                              title={Array.isArray(n.contributors) ? n.contributors.join(", ") : ""}
                            >
                              ×{n.count}
                            </span>
                          )}
                        </div>
                        <div className="text-xs mt-0.5" style={{ color: "var(--wofy-text-soft)" }}>{n.message}</div>
                        <div className="text-[10px] font-mono uppercase tracking-widest mt-1" style={{ color: "var(--wofy-text-soft)" }}>
                          {new Date(n.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </Link>
                  {isMsg && (
                    <div className="mt-2 ml-4">
                      {!isReplying ? (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); startReply(n); }}
                          className="text-xs underline"
                          style={{ color: "var(--wofy-primary)" }}
                          data-testid={`notification-reply-open-${n.id}`}
                        >
                          Reply here
                        </button>
                      ) : (
                        <div
                          className="mt-1 flex gap-2"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`notification-reply-form-${n.id}`}
                        >
                          <input
                            className="wofy-input flex-1 text-sm"
                            placeholder="Type a quick reply…"
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(n); }
                              if (e.key === "Escape") { setReplyingTo(null); }
                            }}
                            maxLength={1000}
                            autoFocus
                            data-testid={`notification-reply-input-${n.id}`}
                          />
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); sendReply(n); }}
                            disabled={sending || !replyText.trim()}
                            className="wofy-btn-primary !px-2 !py-1 text-xs"
                            aria-label="Send reply"
                            data-testid={`notification-reply-send-${n.id}`}
                          >
                            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
